import { Vector3, PerspectiveCamera } from 'three';
import { IEnemy } from '../enemy/IEnemy';
import { FollowCamera } from '../camera/FollowCamera';

export interface ThreatInfo {
  lockedByEnemy: boolean;
  incomingMissiles: boolean;
}

export class HUD {
  private speedEl: HTMLElement;
  private altEl: HTMLElement;
  private hpFill: HTMLElement;
  private hpText: HTMLElement;
  private mslEl: HTMLElement;
  private vulEl: HTMLElement;
  private enemyEl: HTMLElement;
  private lockEl: HTMLElement;
  private warnEl: HTMLElement;
  private lockDot: HTMLElement;
  private threatEl: HTMLElement;

  private maxHp: number;
  private totalEnemies: number;
  private threatBlink = 0;

  constructor(maxHp: number, totalEnemies: number) {
    this.maxHp = maxHp;
    this.totalEnemies = totalEnemies;
    this.speedEl   = document.getElementById('hud-speed')!;
    this.altEl     = document.getElementById('hud-alt')!;
    this.hpFill    = document.getElementById('hud-hp-fill')!;
    this.hpText    = document.getElementById('hud-hp-text')!;
    this.mslEl     = document.getElementById('hud-msl')!;
    this.vulEl     = document.getElementById('hud-vul')!;
    this.enemyEl   = document.getElementById('hud-enemy')!;
    this.lockEl    = document.getElementById('hud-lock-label')!;
    this.warnEl    = document.getElementById('hud-warn')!;
    this.lockDot   = document.getElementById('hud-lockdot')!;

    // 脅威警告要素を作成
    this.threatEl = document.getElementById('hud-threat') || this.createThreatEl();
  }

  private createThreatEl(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'hud-threat';
    el.style.cssText = `
      position: fixed; top: 18%; left: 50%; transform: translateX(-50%);
      font-size: 32px; font-weight: bold; font-family: monospace;
      letter-spacing: 4px;
      pointer-events: none; z-index: 100;
      text-align: center; white-space: pre-line;
      padding: 6px 20px;
    `;
    document.body.appendChild(el);
    return el;
  }

  update(
    speedKmh: number,
    altM: number,
    hp: number,
    vulAmmo: number,
    mslAmmo: number,
    lockedTarget: IEnemy | null,
    followCam: FollowCamera,
    remainingEnemies: number,
    threat?: ThreatInfo,
  ): void {
    this.speedEl.textContent = Math.round(speedKmh).toString();
    this.altEl.textContent   = Math.round(altM).toString();

    const hpPct = Math.max(0, hp / this.maxHp * 100);
    this.hpFill.style.width = `${hpPct}%`;
    this.hpFill.style.background = hpPct > 50 ? '#00ff88' : hpPct > 25 ? '#ffaa00' : '#ff4444';
    this.hpText.textContent = `${Math.round(hp)}`;

    this.mslEl.textContent = mslAmmo.toString();
    this.vulEl.textContent = vulAmmo.toString();
    this.enemyEl.textContent = `${remainingEnemies} / ${this.totalEnemies}`;

    // HP警告
    this.warnEl.textContent = hpPct < 25 ? '⚠ CRITICAL DAMAGE' : '';

    // ロックオンインジケーター（3D→スクリーン座標）
    if (lockedTarget) {
      this.lockEl.textContent = 'LOCKED';
      this.lockEl.style.color = '#ff4444';
      this.updateLockDot(lockedTarget.position, followCam.camera);
    } else {
      this.lockEl.textContent = 'NO LOCK';
      this.lockEl.style.color = '#888';
      this.lockDot.style.display = 'none';
    }

    // 脅威警告
    this.updateThreat(threat);
  }

  private updateThreat(threat?: ThreatInfo): void {
    if (!threat || (!threat.lockedByEnemy && !threat.incomingMissiles)) {
      this.threatEl.style.display = 'none';
      this.threatBlink = 0;
      return;
    }

    this.threatBlink += 1;
    this.threatEl.style.display = 'block';

    if (threat.incomingMissiles) {
      // ミサイル接近: 赤点滅 + 枠付き、高速点滅
      const on = Math.floor(this.threatBlink / 4) % 2 === 0;
      this.threatEl.textContent = '⚠ MISSILE ⚠';
      this.threatEl.style.color = on ? '#ff2222' : '#ff8844';
      this.threatEl.style.textShadow = on ? '0 0 16px #ff0000, 0 0 32px #ff0000' : '0 0 8px #ff4400';
      this.threatEl.style.background = on ? 'rgba(255,0,0,0.15)' : 'rgba(255,0,0,0.05)';
      this.threatEl.style.border = '2px solid rgba(255,50,50,0.6)';
    } else if (threat.lockedByEnemy) {
      // ロックオン警告: 黄色、ゆっくり点滅
      const on = Math.floor(this.threatBlink / 8) % 2 === 0;
      this.threatEl.textContent = 'CAUTION - LOCKED';
      this.threatEl.style.color = on ? '#ffcc00' : '#ff8800';
      this.threatEl.style.textShadow = '0 0 10px #ffaa00';
      this.threatEl.style.background = 'rgba(255,170,0,0.1)';
      this.threatEl.style.border = '2px solid rgba(255,170,0,0.4)';
    }
  }

  private updateLockDot(worldPos: Vector3, camera: PerspectiveCamera): void {
    const v = worldPos.clone().project(camera);
    if (v.z > 1) { this.lockDot.style.display = 'none'; return; }

    const x = ( v.x * 0.5 + 0.5) * window.innerWidth;
    const y = (-v.y * 0.5 + 0.5) * window.innerHeight;

    this.lockDot.style.display = 'block';
    this.lockDot.style.left = `${x - 16}px`;
    this.lockDot.style.top  = `${y - 16}px`;
  }
}
